export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  tags: string[];
  excerpt: string;
  nextSlug?: string;
  nextTitle?: string;
  seoKeyword: string;
  contentHtml: string;
}

export const blogPosts: Record<string, BlogPost> = {
  "ringtrack-build-log": {
    slug: "ringtrack-build-log",
    title: "How I Built a PWA Habit Tracker in One Session",
    date: "June 2025",
    tags: ["PWA", "Build Log"],
    excerpt: "RingTrack started as a sketch I saw in a short video. Six hours later it was live. Here's exactly how I built it, the stack I used, and what I'd change.",
    nextSlug: "groq-vs-openai-solo-builders",
    nextTitle: "Why I Use Groq Instead of OpenAI for Side Projects",
    seoKeyword: "how to build a PWA habit tracker",
    contentHtml: `
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        RingTrack was born out of a midnight itch. I was scrolling through Twitter late at night when I saw a 10-second video clip showing a minimal radial habit tracker. It wasn't an app—it was just a prototype motion graphic. But the visual feedback of completing concentric circles felt infinitely more satisfying than clicking a standard checkbox or filling a boring daily grid.
      </p>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        I decided right then: I wanted this on my phone before I went to sleep. Six hours later, RingTrack was live on Vercel as a fully functional Progressive Web App (PWA). Here is the exact breakdown of how to build a PWA habit tracker in a single focused session.
      </p>

      <h2 class="text-2xl font-bold text-[#f2f2f2] mt-10 mb-4" style="font-family: 'DM Sans', sans-serif;">1. The Stack: Zero Dependencies, Maximum Speed</h2>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        When you are shipping in a single 6-hour sprint, every npm package you install is a liability. I chose vanilla HTML, Tailwind CSS via CDN, inline SVG rendering, Framer Motion for clean micro-interactions, and standard browser <code>localStorage</code> for state persistence.
      </p>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        By using a PWA service worker wrapper, the app installs directly to iOS and Android home screens without requiring App Store approval or complex mobile build pipelines.
      </p>

      <h2 class="text-2xl font-bold text-[#f2f2f2] mt-10 mb-4" style="font-family: 'DM Sans', sans-serif;">2. The Hardest Part: SVG Circular Grid Math</h2>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        Drawing concentric progress rings programmatically required getting intimate with SVG <code>stroke-dasharray</code> and <code>stroke-dashoffset</code>.
      </p>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        For a circle with radius <em>r</em>, the circumference is <em>2 * &pi; * r</em>. When a user marks a habit complete, we calculate the completion fraction <em>p</em> (from 0 to 1) and update the dashoffset:
      </p>
      <pre class="bg-[#161616] border border-[#222222] p-4 rounded text-xs font-mono text-[#888888] overflow-x-auto mb-6">
const radius = 40 + index * 12;
const circumference = 2 * Math.PI * radius;
const strokeDashoffset = circumference - (progress * circumference);
      </pre>

      <h2 class="text-2xl font-bold text-[#f2f2f2] mt-10 mb-4" style="font-family: 'DM Sans', sans-serif;">3. Session 1 vs. Session 2: What Made the Cut</h2>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        In Session 1 (the 6-hour sprint), I built only three things:
      </p>
      <ul class="list-disc list-inside mb-6 space-y-2 text-[#cccccc]">
        <li>Adding up to 5 daily habits</li>
        <li>Tapping a ring segment to mark it complete for today</li>
        <li>Saving the active day's ring states in <code>localStorage</code></li>
      </ul>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        Notice what I left out: analytics, cloud sync, social sharing, user accounts, and streak recovery. I added a simple CSV export in Session 2 a week later, but the core app was already in daily use by myself and 50 early testers.
      </p>

      <h2 class="text-2xl font-bold text-[#f2f2f2] mt-10 mb-4" style="font-family: 'DM Sans', sans-serif;">4. Lessons for Solo Builders</h2>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        If you want to ship fast: limit scope brutally, pick tools with zero setup overhead, and focus on one delightful core interaction. For RingTrack, that interaction was watching the SVG ring snap closed with a subtle haptic vibrate on complete.
      </p>
    `
  },
  "groq-vs-openai-solo-builders": {
    slug: "groq-vs-openai-solo-builders",
    title: "Why I Use Groq Instead of OpenAI for Side Projects",
    date: "July 2025",
    tags: ["AI", "Groq", "Development"],
    excerpt: "Speed, free tier, and Llama 3. For solo builders shipping fast, Groq is the obvious choice right now. Here's my workflow.",
    nextSlug: "building-in-public-my-rules",
    nextTitle: "Building in Public: What I Share, What I Don't",
    seoKeyword: "Groq API vs OpenAI for developers",
    contentHtml: `
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        Over the past year, I have shipped over a dozen AI-powered micro-apps—from ingredient safety checkers to instant nutrition coaches and AI companions. When starting a new side project, the speed of iteration is everything. Every millisecond of latency and every dollar spent on API keys during prototyping friction adds up.
      </p>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        Here is why Groq API vs OpenAI for developers has become my single biggest operational advantage as a solo product builder.
      </p>

      <h2 class="text-2xl font-bold text-[#f2f2f2] mt-10 mb-4" style="font-family: 'DM Sans', sans-serif;">1. Inference Latency: 300+ Tokens Per Second Changes Everything</h2>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        Standard OpenAI endpoints (GPT-4o / GPT-3.5) typically stream back tokens at 30 to 70 tokens per second. Groq's custom LPU (Language Processing Unit) architecture consistently pushes Llama 3 models at 300 to 500+ tokens per second.
      </p>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        For user interfaces, this means responses feel instantaneous. Users don't sit watching a slow blinking cursor for 5 seconds; entire structured paragraphs appear almost instantly.
      </p>

      <h2 class="text-2xl font-bold text-[#f2f2f2] mt-10 mb-4" style="font-family: 'DM Sans', sans-serif;">2. Cost & Prototyping Freedom</h2>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        When building 3 or 4 experimental web tools a month, managing OpenAI API usage limits, billing thresholds, and credit card holds is annoying. Groq's generous free tier allows thousands of daily requests without putting down a credit card.
      </p>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        This freedom lets me deploy single-file HTML/JS applications directly to GitHub Pages or Cloudflare Pages with zero backend setup cost.
      </p>

      <h2 class="text-2xl font-bold text-[#f2f2f2] mt-10 mb-4" style="font-family: 'DM Sans', sans-serif;">3. When Do I Still Use OpenAI?</h2>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        Groq and Llama 3.3 70B handle 90% of web app tasks flawlessly—including text generation, structured JSON extraction, tone adjustment, and basic code writing.
      </p>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        I only reach for OpenAI (GPT-4o) when I require multi-modal vision inputs (analyzing complex photos) or deep multi-step mathematical reasoning that smaller open weights occasionally miss.
      </p>

      <h2 class="text-2xl font-bold text-[#f2f2f2] mt-10 mb-4" style="font-family: 'DM Sans', sans-serif;">4. My Single-File Prototyping Workflow</h2>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        My default stack for testing an AI concept in under 2 hours is:
      </p>
      <ul class="list-disc list-inside mb-6 space-y-2 text-[#cccccc]">
        <li>Single <code>index.html</code> with Tailwind CDN</li>
        <li>Direct <code>fetch()</code> call to Groq's OpenAI-compatible endpoint (<code>https://api.groq.com/openai/v1/chat/completions</code>)</li>
        <li>Model: <code>llama-3.3-70b-versatile</code></li>
        <li>Client-side markdown parser or simple JSON structure parser</li>
      </ul>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        By keeping the stack lean and the inference instant, I can go from idea to live link in a single evening.
      </p>
    `
  },
  "building-in-public-my-rules": {
    slug: "building-in-public-my-rules",
    title: "Building in Public: What I Share, What I Don't",
    date: "August 2025",
    tags: ["Building in Public", "Mindset"],
    excerpt: "I share revenue numbers, failures, and half-finished products. I don't share things that would distract me from shipping. Here's the line I draw.",
    nextSlug: "ringtrack-build-log",
    nextTitle: "How I Built a PWA Habit Tracker in One Session",
    seoKeyword: "building in public for indie developers",
    contentHtml: `
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        "Building in public" has become one of the most misunderstood trends in the indie developer community. On Twitter and LinkedIn, it often degenerates into endless threads about "top 10 AI tools" or performative hustle culture rather than actual product engineering.
      </p>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        As a solo builder from India running FinishAI and shipping multiple side projects, building in public for indie developers has been my primary growth engine. But it only works if you set strict personal boundaries. Here are my rules.
      </p>

      <h2 class="text-2xl font-bold text-[#f2f2f2] mt-10 mb-4" style="font-family: 'DM Sans', sans-serif;">1. What I Share Publicly</h2>
      <ul class="list-disc list-inside mb-6 space-y-2 text-[#cccccc]">
        <li><strong>Raw Work-In-Progress (WIP) Screenshots:</strong> Unfiltered video recordings of apps running on local localhost servers before they are polished.</li>
        <li><strong>Honest Revenue & Metrics:</strong> Exact numbers, MRR milestones, active users, and marketing drop-offs.</li>
        <li><strong>Technical Mistakes & Failures:</strong> Post-mortems of bad stack choices, database migration headaches, or marketing launches that flopped.</li>
        <li><strong>Architecture Decisions:</strong> Why I chose Cloudflare over AWS, or Groq over OpenAI for a specific project.</li>
      </ul>

      <h2 class="text-2xl font-bold text-[#f2f2f2] mt-10 mb-4" style="font-family: 'DM Sans', sans-serif;">2. What I Never Share</h2>
      <ul class="list-disc list-inside mb-6 space-y-2 text-[#cccccc]">
        <li><strong>Unvalidated Half-Baked Ideas:</strong> Announcing "I am building X" before I have even written the first line of code creates artificial dopamine that ruins execution.</li>
        <li><strong>Client & User Confidential Data:</strong> Proprietary data from FinishAI business customers or private artist feedback from Curvory.</li>
        <li><strong>Endless Social Threads That Distract From Code:</strong> If posting content takes more than 30 minutes a day, it is actively hurting your shipping velocity.</li>
      </ul>

      <h2 class="text-2xl font-bold text-[#f2f2f2] mt-10 mb-4" style="font-family: 'DM Sans', sans-serif;">3. The Golden Rule: Ship First, Share Second</h2>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        In the indie hacking space, talk is cheap. The internet is full of people with gorgeous pitch decks and 10,000 Twitter followers who have never shipped a single profitable, working product.
      </p>
      <p class="mb-6 leading-relaxed text-[#cccccc]">
        My rule is simple: <strong>Never tweet about a feature until it is pushed to production or running on a live staging URL.</strong> Proof of work speaks louder than any announcement thread.
      </p>
    `
  }
};
